import { CSSProperties, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./UtilityEmailPreview.module.scss";
import { ReactComponent as EmailBanner } from "../../../../../assets/background/email-template-banner.svg";
import { EmailTemplateFormValues } from "../../../../../model/bps-admin/email-template-configuration";

/** The src of a form image: the stored URL, or a picked file shown from memory until Save. */
function useImageUrl(image: File | string | null) {
	const [url, setUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!image || typeof image === "string") {
			setUrl(image);
			return;
		}

		const objectUrl = URL.createObjectURL(image);
		setUrl(objectUrl);
		return () => URL.revokeObjectURL(objectUrl);
	}, [image]);

	return url;
}

interface UtilityEmailPreviewProps {
	values: EmailTemplateFormValues;
	companyName: string;
}

/**
 * Mock of the outgoing email, following the backend's XeppEmailLayout.cshtml: banner, logo
 * with its wordmark, content card, footer and bottom color line. Driven by the unsaved form,
 * so every toggle, picked file and color shows before Save.
 *
 * The banner is the layout's SVG (its frame + 2 wave shapes tinted with primary/secondary/
 * accent color via CSS custom properties the SVG's fills reference), not the uploaded banner
 * image — the sent email never renders that image either.
 */
export function UtilityEmailPreview({ values, companyName }: Readonly<UtilityEmailPreviewProps>) {
	const { t } = useTranslation("bpsAdmin");
	const logoUrl = useImageUrl(values.logo);
	const logoTextUrl = useImageUrl(values.logoText);
	const bannerColorVars = {
		"--email-primary-color": values.primaryColor,
		"--email-secondary-color": values.secondaryColor,
		"--email-accent-color": values.accentColor,
	} as CSSProperties;

	return (
		<div className={styles.email} style={{ color: values.fontColor }}>
			{values.bannerEnabled && <EmailBanner className={styles.banner} style={bannerColorVars} />}

			{values.logoEnabled && (
				<div className={styles.logo}>
					{logoUrl && <img className={styles.logoIcon} src={logoUrl} alt={companyName} />}
					{logoTextUrl && <img className={styles.logoText} src={logoTextUrl} alt={companyName} />}
				</div>
			)}

			<div className={styles.card}>
				<h4 className={styles.title}>{t("utilitySetup.emailTemplate.preview.title")}</h4>
				<p>{t("utilitySetup.emailTemplate.preview.body")}</p>
				<p className={styles.signature}>
					{t("utilitySetup.emailTemplate.preview.bestRegards")}
					<br />
					<span className={styles.supportTeam}>
						{t("utilitySetup.emailTemplate.preview.supportTeam", { companyName })}
					</span>
				</p>
			</div>

			<p className={styles.footer}>{t("utilitySetup.emailTemplate.preview.footerLinks")}</p>
			<p className={styles.companyName}>{companyName}</p>
			<p className={styles.footer}>
				{t("utilitySetup.emailTemplate.preview.allRightsReserved", {
					year: new Date().getFullYear(),
					companyName,
				})}
			</p>

			<div className={styles.bottomLine} style={{ backgroundColor: values.primaryColor }} />
		</div>
	);
}
